import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useNavigate } from "react-router-dom";

function HeroSection() {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden rounded-3xl mx-4 lg:mx-8 mt-4">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20background%20with%20golden%20geometric%20lines%20and%20nodes%20flowing%20like%20data%20streams%2C%20deep%20charcoal%20black%20base%20with%20warm%20gold%20accent%20highlights%2C%20fintech%20cybernetic%20aesthetic%2C%20minimalist%20editorial%20style%2C%20soft%20glow%20effects%2C%20no%20text&width=1600&height=900&seq=hero-bg-001&orientation=landscape"
          alt="金融数据流抽象背景"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-4xl">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary-500/40 bg-primary-500/10 backdrop-blur-sm mb-6">
          <span className="w-2 h-2 rounded-full bg-primary-500" />
          <span className="text-background-50 text-xs font-medium tracking-wide">
            銀行データカード処理の専門家
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-4">
          金融データ接続を
          <br />
          再構築する
        </h1>

        <p className="text-base md:text-lg text-background-300 font-light mb-8 max-w-2xl mx-auto leading-relaxed">
          安全かつ効率的なカード処理ソリューションで、
          <br className="hidden md:block" />
          日本の金融機関のデジタルトランスフォーメーションを支援
        </p>

        <button
          onClick={() => navigate("/services")}
          className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center mx-auto hover:bg-primary-600 transition-all duration-300 hover:scale-110"
          aria-label="了解更多"
        >
          <i className="ri-arrow-right-line text-background-50 text-xl" />
        </button>
      </div>
    </section>
  );
}

function AboutSection() {
  const { ref, isVisible } = useScrollAnimation();
  const navigate = useNavigate();

  return (
    <section
      ref={ref}
      className={`py-20 md:py-28 px-4 lg:px-8 animate-on-scroll ${
        isVisible ? "is-visible" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left Content */}
        <div>
          <div className="inline-flex items-center px-3 py-1.5 rounded-md bg-primary-100 mb-6">
            <span className="text-primary-700 text-xs font-bold uppercase tracking-wider">
              私たちについて
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
            信頼と革新を
            <br />
            基盤にした
            <br />
            データ処理
          </h2>

          <p className="text-foreground-600 text-base leading-relaxed mb-4">
            株式会社銀行データカードは、2008年の設立以来、日本の金融機関向けに高度なデータカード処理ソリューションを提供してきました。
            最新の暗号化技術とAI駆動のデータ分析により、
            安全かつ効率的な金融データ管理を実現しています。
          </p>
          <p className="text-foreground-600 text-base leading-relaxed mb-6">
            お問い合わせは{" "}
            <a
              href="mailto:admin@bankcheckdata-card.jp"
              className="text-primary-500 font-medium hover:text-primary-600 transition-colors"
            >
              admin@bankcheckdata-card.jp
            </a>
            まで
          </p>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-shield-check-line text-background-50 text-lg" />
              </div>
              <div>
                <p className="text-foreground-950 font-bold text-lg">99.9%</p>
                <p className="text-foreground-500 text-xs">稼働率</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-bank-line text-background-50 text-lg" />
              </div>
              <div>
                <p className="text-foreground-950 font-bold text-lg">200+</p>
                <p className="text-foreground-500 text-xs">金融機関</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Card */}
        <div className="bg-background-100 rounded-2xl p-8 lg:p-10 relative">
          <div className="flex items-center justify-between mb-8">
            <span className="text-foreground-400 text-xs font-medium">
              企業メッセージ
            </span>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-primary-500" />
              <span className="w-2 h-2 rounded-full bg-foreground-300" />
              <span className="w-2 h-2 rounded-full bg-foreground-200" />
            </div>
          </div>

          <p className="text-foreground-950 text-lg md:text-xl font-medium leading-relaxed mb-8">
            "データの正確性とセキュリティは、現代金融の生命線です。
            私たちは常に一歩先を行く技術で、お客様の信頼に応え続けます。"
          </p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20businessman%20portrait%20in%20dark%20suit%2C%20neutral%20studio%20background%2C%20confident%20expression%2C%20corporate%20headshot%20style%2C%20soft%20lighting%2C%20high%20quality&width=100&height=100&seq=ceo-avatar-001&orientation=squarish"
                alt="代表取締役"
                className="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <p className="text-foreground-950 font-medium text-sm">
                  田中 健一
                </p>
                <p className="text-foreground-500 text-xs">代表取締役 CEO</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/contact")}
              className="w-10 h-10 rounded-lg bg-background-200/70 flex items-center justify-center hover:bg-primary-500 hover:text-background-50 transition-all"
            >
              <i className="ri-arrow-right-line text-foreground-600" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const { ref, isVisible } = useScrollAnimation();
  const navigate = useNavigate();

  const services = [
    {
      icon: "ri-database-2-line",
      title: "データ処理",
      desc: "大規模金融データの高速処理とリアルタイム分析を実現。日次処理能力は1億件以上。",
    },
    {
      icon: "ri-shield-keyhole-line",
      title: "セキュリティ",
      desc: "AES-256暗号化と多層認証システムで、金融データの最高レベルの保護を提供。",
    },
    {
      icon: "ri-file-shield-line",
      title: "コンプライアンス",
      desc: "JIS Q 27001認証取得。金融庁ガイドラインに完全準拠した運用を徹底。",
    },
  ];

  return (
    <section
      ref={ref}
      className={`py-20 md:py-28 px-4 lg:px-8 bg-background-100 animate-on-scroll ${
        isVisible ? "is-visible" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <p className="text-foreground-500 text-sm font-medium mb-3">
            事業領域
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
            次世代の金融
            <br />
            インフラを構築
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-background-50 rounded-xl p-6 lg:p-8 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-5">
                <i className={`${service.icon} text-primary-500 text-xl`} />
              </div>
              <h3 className="text-foreground-950 font-bold text-lg mb-3">
                {service.title}
              </h3>
              <p className="text-foreground-600 text-sm leading-relaxed">
                {service.desc}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <button
            onClick={() => navigate("/services")}
            className="inline-flex items-center gap-2 text-primary-500 font-medium text-sm hover:text-primary-600 transition-colors"
          >
            すべてのサービスを見る
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
}

function TestimonialSection() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section
      ref={ref}
      className={`py-20 md:py-28 px-4 lg:px-8 animate-on-scroll ${
        isVisible ? "is-visible" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center">
        {/* Left Image */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl overflow-hidden aspect-square">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20fintech%20office%20interior%20with%20warm%20golden%20ambient%20lighting%2C%20glass%20walls%2C%20sleek%20workstations%2C%20abstract%20golden%20light%20streaks%2C%20dark%20moody%20atmosphere%2C%20professional%20corporate%20environment%2C%20editorial%20photography%20style&width=800&height=800&seq=testimonial-img-001&orientation=squarish"
              alt="オフィス環境"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Right Content */}
        <div className="lg:col-span-3">
          <p className="text-foreground-500 text-sm mb-4">
            (お客様の声)
          </p>
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-6">
            信頼できる
            <br />
            <span className="text-foreground-400">パートナー</span>
          </h3>
          <p className="text-foreground-600 text-base leading-relaxed mb-6 pl-4 border-l-2 border-primary-500">
            "BankCheckDataの導入以来、カード決済処理のエラー率が80%減少し、
            顧客満足度が大幅に向上しました。専門的なサポート体制と
            迅速な対応が、私たちの事業成長に不可欠です。"
          </p>
          <p className="text-primary-500 font-bold text-sm">
            — 佐藤 美咲, 三菱UFJ銀行 デジタル部門部長
          </p>

          <div className="flex items-center gap-3 mt-8">
            <button
              className="w-10 h-10 rounded-full border border-foreground-200 flex items-center justify-center text-foreground-400 hover:border-primary-500 hover:text-primary-500 transition-colors"
              aria-label="前へ"
            >
              <i className="ri-arrow-left-line" />
            </button>
            <button
              className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-background-50 hover:bg-primary-600 transition-colors"
              aria-label="次へ"
            >
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const { ref, isVisible } = useScrollAnimation();
  const navigate = useNavigate();

  return (
    <section
      ref={ref}
      className={`relative mx-4 lg:mx-8 rounded-3xl overflow-hidden min-h-[500px] flex items-center animate-on-scroll ${
        isVisible ? "is-visible" : ""
      }`}
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20abstract%20background%20with%20warm%20golden%20light%20trails%20and%20floating%20particles%2C%20deep%20black%20base%20with%20gold%20bokeh%20effects%2C%20fintech%20data%20stream%20visualization%2C%20cinematic%20atmosphere%2C%20no%20text&width=1600&height=700&seq=cta-bg-001&orientation=landscape"
          alt="CTA背景"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-8 lg:px-16 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-end">
          <div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-6">
              スマートな
              <br />
              データ処理を始める
            </h2>
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center gap-3 bg-background-50 rounded-full pl-6 pr-2 py-2 hover:bg-background-100 transition-colors"
            >
              <span className="text-foreground-950 font-medium text-sm">
                今すぐ相談する
              </span>
              <div className="w-10 h-10 rounded-full bg-foreground-950 flex items-center justify-center">
                <i className="ri-arrow-right-line text-background-50" />
              </div>
            </button>
          </div>
          <div>
            <p className="text-background-300 text-base leading-relaxed max-w-md">
              導入から運用まで、専門チームがフルサポート。
              カスタマイズされたソリューションで、あなたのビジネスの
              デジタル化を加速させます。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen bg-background-50">
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <TestimonialSection />
      <CTASection />
    </div>
  );
}